import { BaseSupabaseService } from './BaseSupabaseService';
import {
  Student,
  StudentListItem,
  PaginatedResponse,
  mapStudentClassificationFromColumn,
  type StudentConsultancyCreditState,
} from '../types';
import type {
  StudentBehavioralPillars,
  StudentBehavioralProfile,
  StudentBehavioralTag,
} from '../types/studentBehavioralProfile';
import type {
  PerformanceSupportReason,
  PerformanceSupportState,
} from '../types/performanceSupport';
import type { SensitivePeriodState } from '../types/sensitiveStudent';
import { handleServiceError } from '../utils/errorHandler';
import { mapProjectIdentity } from '../utils/project';
import { readIsRecentlyReactivated } from '../utils/studentReactivation';
import {
  readIsPregnant,
  readIsRedFlag,
  readIsSensitive,
  readPregnantUntil,
  readSensitiveReason,
} from '../utils/studentSensitive';
import { requestEdgeFunction } from './edgeFunctionClient';

type StudentControllerAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'delete-student'
  | 'change-student-status'
  | 'grant-student-consultancy-credit'
  | 'undo-student-consultancy-credit'
  | 'get-student-behavioral-profile'
  | 'save-student-behavioral-profile'
  | 'get-performance-support'
  | 'save-performance-support'
  | 'update-performance-support'
  | 'update-pregnancy-period'
  | 'get-sensitive-period'
  | 'start-sensitive-period'
  | 'update-sensitive-period'
  | 'end-sensitive-period';

interface StudentControllerResponse<T> {
  success?: boolean;
  data?: T;
  error?: {
    code?: string;
    message?: string;
  };
}

export interface StudentProfilePersistencePayload {
  studentId?: number | null;
  student: Record<string, unknown>;
  phone?: Record<string, unknown> | null;
  address?: Record<string, unknown> | null;
  coachId?: number | null;
}

class StudentServiceClass extends BaseSupabaseService<Student> {
  constructor() {
    super('students');
  }

  private async executeStudentAction<T>(
    action: StudentControllerAction,
    payload: Record<string, unknown>,
    context: string,
  ): Promise<T> {
    const body = await requestEdgeFunction<StudentControllerResponse<T>>('students', {
      method: 'POST',
      body: { action, payload },
    }).catch((error) => {
      handleServiceError(error, context);
      throw error;
    });

    if (body.success === false) {
      handleServiceError(
        new Error(body.error?.message || `Erro ao executar ação ${action} de aluna`),
        context,
      );
    }

    return body.data as T;
  }

  override async create(payload: Partial<Student>): Promise<Student> {
    const result = await this.executeStudentAction<{ student: Student }>(
      'create',
      { student: payload },
      'cadastrar aluna',
    );

    return result.student;
  }

  override async update(id: number | string, payload: Partial<Student>): Promise<Student> {
    const result = await this.executeStudentAction<{ student: Student }>(
      'update',
      { id, student: payload },
      'atualizar aluna',
    );

    return result.student;
  }

  private async findListingTagsByStudentId(studentId: number): Promise<{
    project: Student['project'];
    is_reactivated: boolean;
  }> {
    try {
      const body = await requestEdgeFunction<StudentControllerResponse<unknown>>('students', {
        method: 'POST',
        body: {
          action: 'get-student-project',
          payload: { studentId },
        },
      });

      return {
        project: mapProjectIdentity(body.data ?? null),
        is_reactivated: readIsRecentlyReactivated(body.data),
      };
    } catch {
      return {
        project: null,
        is_reactivated: false,
      };
    }
  }

  override async findById(id: number | string): Promise<Student | null> {
    const numericId = Number(id);
    const [student, listingTags] = await Promise.all([
      super.findById(id),
      Number.isInteger(numericId) && numericId > 0
        ? this.findListingTagsByStudentId(numericId)
        : Promise.resolve({ project: null, is_reactivated: false }),
    ]);
    if (!student) return null;

    const rawClassification = (student as Student & {
      classification?: string | Student['classification'];
    }).classification;

    const columnStatus = typeof rawClassification === 'string'
      ? rawClassification
      : rawClassification?.status;

    return {
      ...student,
      classification: mapStudentClassificationFromColumn(columnStatus) ?? undefined,
      project: listingTags.project,
      is_reactivated: listingTags.is_reactivated,
    };
  }

  async saveProfile(payload: StudentProfilePersistencePayload): Promise<Student> {
    const action: StudentControllerAction = payload.studentId ? 'update' : 'create';
    const result = await this.executeStudentAction<{ student: Student; studentId: number }>(
      action,
      {
        id: payload.studentId ?? undefined,
        studentId: payload.studentId ?? undefined,
        student: payload.student,
        phone: payload.phone ?? undefined,
        address: payload.address ?? undefined,
        coachId: payload.coachId ?? undefined,
      },
      action === 'create' ? 'cadastrar aluna' : 'atualizar aluna',
    );

    return result.student;
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: {
      searchName?: string;
      filterStatus?: string;
      filterLevel?: string;
      filterIsInactive?: string;
      sortOrder?: string;
      coachIds?: string[];
      projectCodes?: string[];
      sensitiveStatuses?: string[];
      redFlag?: 'all' | 'yes';
    } = {}
  ): Promise<PaginatedResponse<StudentListItem>> {
    const body = await requestEdgeFunction<PaginatedResponse<
      Omit<StudentListItem, 'project'> & { project?: unknown }
    >>('llist-paginated-students', {
      method: 'GET',
      query: {
        page,
        pageSize,
        searchName: filters.searchName,
        filterStatus: filters.filterStatus,
        filterLevel: filters.filterLevel,
        filterIsInactive: filters.filterIsInactive,
        sortOrder: filters.sortOrder,
        coachIds: filters.coachIds?.length ? filters.coachIds : undefined,
        projectCodes: filters.projectCodes?.length ? filters.projectCodes : undefined,
        sensitiveStatuses: filters.sensitiveStatuses?.length
          ? filters.sensitiveStatuses
          : undefined,
        redFlag: filters.redFlag === 'yes' ? 'yes' : undefined,
      },
    }).catch((error) => {
      handleServiceError(error, 'listar alunas');
      throw error;
    });

    return {
      pagination: body.pagination,
      data: body.data.map((student) => ({
        ...student,
        project: mapProjectIdentity(student.project) ?? mapProjectIdentity(student),
        is_reactivated: readIsRecentlyReactivated(student),
        is_red_flag: readIsRedFlag(student),
        is_sensitive: readIsSensitive(student),
        sensitive_reason: readSensitiveReason(student),
        is_pregnant: readIsPregnant(student),
        pregnant_until: readPregnantUntil(student),
      })),
    };
  }

  async findByUserId(userId: string): Promise<Student | null> {
    const results = await this.findAll({
      filters: [{ column: 'user_id', value: userId }],
    });
    return results[0] || null;
  }

  async updateStatus(
    studentId: number,
    isInactive: boolean,
    reason?: string,
    observation?: string | null,
  ): Promise<void> {
    const trimmedObservation = observation?.trim() || null;
    await this.executeStudentAction<{ isInactive: boolean }>(
      'change-student-status',
      {
        studentId,
        isInactive,
        ...(reason ? { reason } : {}),
        ...(trimmedObservation ? { observation: trimmedObservation } : {}),
      },
      'alterar status da aluna',
    );
  }

  async getConsultancyCredit(studentId: number): Promise<StudentConsultancyCreditState> {
    const body = await requestEdgeFunction<StudentControllerResponse<StudentConsultancyCreditState>>('students', {
      method: 'POST',
      body: {
        action: 'get-student-consultancy-credit',
        payload: { studentId },
      },
    }).catch((error) => {
      handleServiceError(error, 'carregar crédito da aluna');
      throw error;
    });

    return body.data ?? { hasCredit: false, credit: null };
  }

  async grantConsultancyCredit(
    studentId: number,
    validUntil: string,
  ): Promise<StudentConsultancyCreditState> {
    return this.executeStudentAction<StudentConsultancyCreditState>(
      'grant-student-consultancy-credit',
      { studentId, validUntil },
      'iniciar período de crédito da aluna',
    );
  }

  async undoConsultancyCredit(studentId: number): Promise<StudentConsultancyCreditState> {
    return this.executeStudentAction<StudentConsultancyCreditState>(
      'undo-student-consultancy-credit',
      { studentId },
      'desfazer crédito da aluna',
    );
  }

  async getBehavioralProfile(studentId: number): Promise<StudentBehavioralProfile> {
    return this.executeStudentAction<StudentBehavioralProfile>(
      'get-student-behavioral-profile',
      { studentId },
      'carregar perfil comportamental',
    );
  }

  async saveBehavioralProfile(
    studentId: number,
    profile: {
      tags: StudentBehavioralTag[];
      pillars: StudentBehavioralPillars;
      exitRisk: StudentBehavioralProfile['exitRisk'];
      reversible: StudentBehavioralProfile['reversible'];
    },
  ): Promise<StudentBehavioralProfile> {
    return this.executeStudentAction<StudentBehavioralProfile>(
      'save-student-behavioral-profile',
      {
        studentId,
        tags: profile.tags,
        pillars: profile.pillars,
        exitRisk: profile.exitRisk,
        reversible: profile.reversible,
      },
      'salvar perfil comportamental',
    );
  }

  async getPerformanceSupport(studentId: number): Promise<PerformanceSupportState> {
    return this.executeStudentAction<PerformanceSupportState>(
      'get-performance-support',
      { studentId },
      'carregar suporte da performance',
    );
  }

  async savePerformanceSupport(
    studentId: number,
    reason: PerformanceSupportReason,
    pregnantFrom?: string | null,
  ): Promise<{ id: number; reason: PerformanceSupportReason }> {
    return this.executeStudentAction<{ id: number; reason: PerformanceSupportReason }>(
      'save-performance-support',
      {
        studentId,
        reason,
        ...(pregnantFrom ? { pregnantFrom } : {}),
      },
      'salvar suporte da performance',
    );
  }

  async updatePerformanceSupport(
    studentId: number,
    reason: PerformanceSupportReason,
    pregnantFrom?: string | null,
  ): Promise<{ id: number; reason: PerformanceSupportReason }> {
    return this.executeStudentAction<{ id: number; reason: PerformanceSupportReason }>(
      'update-performance-support',
      {
        studentId,
        reason,
        ...(pregnantFrom ? { pregnantFrom } : {}),
      },
      'alterar suporte da performance',
    );
  }

  async updatePregnancyPeriod(
    studentId: number,
    pregnancyPeriodId: number,
    pregnantFrom: string,
  ): Promise<{ id: number; pregnantFrom: string; pregnantUntil: string }> {
    return this.executeStudentAction<{ id: number; pregnantFrom: string; pregnantUntil: string }>(
      'update-pregnancy-period',
      { studentId, pregnancyPeriodId, pregnantFrom },
      'atualizar data da gravidez',
    );
  }

  async getSensitivePeriod(studentId: number): Promise<SensitivePeriodState> {
    return this.executeStudentAction<SensitivePeriodState>(
      'get-sensitive-period',
      { studentId },
      'carregar aluna sensível',
    );
  }

  async startSensitivePeriod(
    studentId: number,
    reason: string,
  ): Promise<SensitivePeriodState['current']> {
    return this.executeStudentAction<SensitivePeriodState['current']>(
      'start-sensitive-period',
      { studentId, reason },
      'iniciar período sensível',
    );
  }

  async updateSensitivePeriod(
    studentId: number,
    reason: string,
  ): Promise<SensitivePeriodState['current']> {
    return this.executeStudentAction<SensitivePeriodState['current']>(
      'update-sensitive-period',
      { studentId, reason },
      'atualizar período sensível',
    );
  }

  async endSensitivePeriod(
    studentId: number,
    endJustification: string,
  ): Promise<{ id: number }> {
    return this.executeStudentAction<{ id: number }>(
      'end-sensitive-period',
      { studentId, endJustification },
      'encerrar período sensível',
    );
  }

  async getLatestInactivationReason(studentId: number): Promise<{
    reason: string | null;
    observation: string | null;
  }> {
    try {
      const body = await requestEdgeFunction<StudentControllerResponse<{
        reason: string | null;
        observation: string | null;
      }>>('students', {
        method: 'POST',
        body: {
          action: 'get-latest-inactivation-reason',
          payload: { studentId },
        },
      });

      return {
        reason: body.data?.reason ?? null,
        observation: body.data?.observation ?? null,
      };
    } catch (error) {
      handleServiceError(error, 'buscar motivo de inativação');
      return { reason: null, observation: null };
    }
  }

  async checkCpfExists(cpf: string, excludeId?: number): Promise<boolean> {
    if (!cpf) return false;
    const results = await this.findAll({
      filters: [{ column: 'cpf', value: cpf }],
    });
    if (excludeId) {
      return results.some((s) => s.id !== excludeId);
    }
    return results.length > 0;
  }

  async deleteWithRelations(studentId: number): Promise<void> {
    await this.executeStudentAction(
      'delete-student',
      { id: studentId, studentId },
      'excluir aluna',
    );
  }

  async getCoachAssociation(studentId: number): Promise<{ coach_id: number | null; coach_name: string | null }> {
    const body = await requestEdgeFunction<{ coach_id: number | null; coach_name: string | null }>('manage-student-coach', {
      method: 'GET',
      query: { studentId },
    }).catch((error) => {
      handleServiceError(error, 'carregar professora da aluna');
      throw error;
    });

    return {
      coach_id: body.coach_id ?? null,
      coach_name: body.coach_name ?? null,
    };
  }

  async upsertCoachAssociation(
    studentId: number,
    coachId: number,
  ): Promise<{ coach_id: number | null; coach_name: string | null }> {
    const body = await requestEdgeFunction<{ coach_id: number | null; coach_name: string | null }>('manage-student-coach', {
      method: 'POST',
      body: { studentId, coachId },
    }).catch((error) => {
      handleServiceError(error, 'salvar professora da aluna');
      throw error;
    });

    return {
      coach_id: body.coach_id ?? null,
      coach_name: body.coach_name ?? null,
    };
  }

  async findAllWithEmails(): Promise<Array<{ id: number; full_name: string; email: string }>> {
    try {
      const body = await requestEdgeFunction<StudentControllerResponse<Array<{ id: number; full_name: string; email: string }>>>('students', {
        method: 'POST',
        body: {
          action: 'get-students-with-emails',
          payload: {},
        },
      });

      return (body.data ?? []).filter((student) => student.email?.trim());
    } catch (error) {
      handleServiceError(error, 'buscar alunas com e-mail');
      return [];
    }
  }
}

export const StudentService = new StudentServiceClass();
